'use client'

import React from 'react'

import { Loader2 } from 'lucide-react'

import {
  Select as BaseSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

import { isFieldInvalid } from '@/utils'

import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

type SelectProps<T> = {
  disabled?: boolean
  label?: string
  loading?: boolean
  optionKey: keyof T
  options: T[]
  optionValue: keyof T
  placeholder?: string
  ref?: React.Ref<HTMLButtonElement>
  renderOption?: (item: T) => React.ReactNode
  required?: boolean
}

export const Select = <T extends object>({
  disabled,
  label,
  loading,
  optionKey,
  options,
  optionValue,
  placeholder,
  ref,
  renderOption,
  required
}: SelectProps<T>) => {
  const field = useFieldContext<number | string | string[] | undefined>()
  const isInvalid = isFieldInvalid(field)

  const value = field.state.value?.toString() ?? ''
  const isDisabled = disabled || loading

  const selectElement = (
    <BaseSelect
      disabled={isDisabled}
      onValueChange={(val) => {
        // Disabled/loading: Radix can still emit "" or churn; never clobber form state.
        if (isDisabled) {
          return
        }
        if (val === '' && required) {
          return
        }
        field.handleChange(val)
      }}
      value={value}
    >
      <SelectTrigger
        aria-busy={loading || undefined}
        aria-invalid={isInvalid || undefined}
        className="h-[42px] w-full max-w-full min-w-0 rounded-md! !px-3.5 !py-2"
        data-invalid={isInvalid || undefined}
        data-loading={loading || undefined}
        id={field.name}
        onBlur={field.handleBlur}
        ref={ref}
        style={{ borderRadius: '7px' }}
      >
        {loading && (
          <Loader2 className="text-muted-foreground me-2 h-4 w-4 animate-spin" />
        )}
        <SelectValue placeholder={loading ? '' : placeholder} />
      </SelectTrigger>

      <SelectContent className="w-[--radix-popover-trigger-width] max-w-fit p-0">
        {loading ? (
          <div className="text-muted-foreground flex items-center justify-center gap-2 p-3 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : (
          (Array.isArray(options) ? options : []).map((item) => (
            <SelectItem
              className="flex items-center gap-3 px-3 py-2"
              key={String(item[optionKey])}
              value={String(item[optionKey])}
            >
              {renderOption
                ? renderOption(item)
                : (item[optionValue] as string)}{' '}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </BaseSelect>
  )

  return (
    <FieldWrapper field={field} label={label} required={required}>
      {selectElement}
    </FieldWrapper>
  )
}
